export class Navigation {
  constructor(options = {}) {
    this.options = {
      enableKeyboardShortcuts: true,
      enableTooltips: true,
      ...options
    };
    this.panels = new Map();
    this.activePanel = null;
    this.init();
  }

  init() {
    this.setupPanels();
    this.attachEventListeners();

    if (this.options.enableKeyboardShortcuts) {
      this.setupKeyboardShortcuts();
    }
  }

  setupPanels() {
    // Register all panels that can be controlled
    this.registerPanel('menu', {
      trigger: '#menuToggle',
      panel: '#sidebar',
      overlay: '#sidebarOverlay',
      toggleClass: 'visible'
    });

    this.registerPanel('filter', {
      trigger: '#filterToggle',
      panel: '#filterPanel',
      toggleClass: 'visible'
    });

    this.registerPanel('legend', {
      trigger: '#legendToggle',
      panel: '#legendPanel',
      toggleClass: 'visible'
    });

    this.registerPanel('search', {
      trigger: '#searchToggle',
      panel: '#searchContainer',
      toggleClass: 'visible'
    });

    // Register modal panels
    this.registerPanel('contact', {
      trigger: '#contactToggle',
      action: () => window.contactUs?.show()
    });

    this.registerPanel('premium', {
      trigger: '#premiumToggle',
      action: () => window.premiumMembers?.show()
    });
  }

  registerPanel(name, config) {
    const panel = {
      name,
      trigger: document.querySelector(config.trigger),
      panel: document.querySelector(config.panel),
      overlay: config.overlay ? document.querySelector(config.overlay) : null,
      toggleClass: config.toggleClass || 'visible',
      action: config.action, // Support for custom action functions
      isVisible: false
    };

    // For panels with custom actions, only trigger is required
    if (panel.trigger && (panel.panel || panel.action)) {
      this.panels.set(name, panel);
    }
  }

  attachEventListeners() {
    // Panel toggle buttons
    this.panels.forEach((panel) => {
      if (panel.trigger) {
        panel.trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.togglePanel(panel.name);
        });
      }

      // Close buttons within panels
      if (panel.panel) {
        const closeButtons = panel.panel.querySelectorAll('.sidebar-close, .panel-close');
        closeButtons.forEach(btn => {
          btn.addEventListener('click', () => {
            this.hidePanel(panel.name);
          });
        });
      }

      // Overlay clicks
      if (panel.overlay) {
        panel.overlay.addEventListener('click', () => {
          this.hidePanel(panel.name);
        });
      }
    });

    // Special buttons
    this.setupSpecialButtons();

    // Global ESC key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideAllPanels();
      }
    });

    // Click outside to close panels
    document.addEventListener('click', (e) => {
      this.handleOutsideClick(e);
    });
  }

  setupSpecialButtons() {
    // Reset view button
    const resetViewBtn = document.getElementById('resetViewBtn');
    if (resetViewBtn) {
      resetViewBtn.addEventListener('click', () => {
        this.resetMapView();
      });
    }

    // Zoom controls
    const zoomInBtn = document.getElementById('zoomInBtn');
    const zoomOutBtn = document.getElementById('zoomOutBtn');

    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => {
        this.zoomIn();
      });
    }

    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => {
        this.zoomOut();
      });
    }
  }

  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Only handle shortcuts if no input is focused
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Handle keyboard shortcuts
      switch (e.key.toLowerCase()) {
        case 'f':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            this.togglePanel('search');
          } else {
            this.togglePanel('filter');
          }
          break;

        case 'l':
          this.togglePanel('legend');
          break;

        case 'm':
          this.togglePanel('menu');
          break;

        case 'r':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            this.resetMapView();
          }
          break;

        case '=':
        case '+':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            this.zoomIn();
          }
          break;

        case '-':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            this.zoomOut();
          }
          break;
      }
    });
  }

  togglePanel(panelName) {
    const panel = this.panels.get(panelName);
    if (!panel) return;

    if (panel.isVisible) {
      this.hidePanel(panelName);
    } else {
      this.showPanel(panelName);
    }
  }

  showPanel(panelName) {
    const panel = this.panels.get(panelName);
    if (!panel || panel.isVisible) return;

    // Handle custom action functions (for modals)
    if (panel.action && typeof panel.action === 'function') {
      panel.action();
      return;
    }

    // Hide other panels first (except search which can coexist)
    if (panelName !== 'search') {
      this.hideAllPanels(['search']);
    }

    // Show the panel
    if (panel.panel) {
      panel.panel.classList.add(panel.toggleClass);
      if (panel.overlay) {
        panel.overlay.classList.add(panel.toggleClass);
      }

      // Update button state
      if (panel.trigger) {
        panel.trigger.classList.add('active');
      }

      panel.isVisible = true;
      this.activePanel = panelName;
    }

    // Load statistics when menu/sidebar is shown
    if (panelName === 'menu') {
      this.loadStatistics();
    }

    // Dispatch event
    const event = new CustomEvent('panelShown', {
      detail: { panel: panelName }
    });
    document.dispatchEvent(event);

    // Update tooltips
    this.updateTooltip(panel.trigger, 'Hide');
  }

  hidePanel(panelName) {
    const panel = this.panels.get(panelName);
    if (!panel || !panel.isVisible) return;

    // Hide the panel
    panel.panel.classList.remove(panel.toggleClass);
    if (panel.overlay) {
      panel.overlay.classList.remove(panel.toggleClass);
    }

    // Update button state
    if (panel.trigger) {
      panel.trigger.classList.remove('active');
    }

    panel.isVisible = false;

    if (this.activePanel === panelName) {
      this.activePanel = null;
    }

    // Dispatch event
    const event = new CustomEvent('panelHidden', {
      detail: { panel: panelName }
    });
    document.dispatchEvent(event);

    // Update tooltips
    this.updateTooltip(panel.trigger, this.getDefaultTooltip(panelName));
  }

  hideAllPanels(except = []) {
    this.panels.forEach((panel, name) => {
      if (!except.includes(name) && panel.isVisible) {
        this.hidePanel(name);
      }
    });
  }

  handleOutsideClick(e) {
    // Don't close if clicking on a trigger button
    if (e.target.closest('.control-button')) {
      return;
    }

    // Check if click is outside all visible panels
    let clickedOutside = true;
    this.panels.forEach((panel) => {
      if (panel.isVisible && (
        e.target.closest(`#${panel.panel.id}`) ||
        (panel.overlay && e.target.closest(`#${panel.overlay.id}`))
      )) {
        clickedOutside = false;
      }
    });

    if (clickedOutside) {
      this.hideAllPanels();
    }
  }

  resetMapView() {
    // Dispatch event for map to handle
    const event = new CustomEvent('resetMapView');
    document.dispatchEvent(event);

    // Show notification
    this.showNotification('Map view reset to default position');
  }

  zoomIn() {
    const event = new CustomEvent('mapZoom', {
      detail: { direction: 'in' }
    });
    document.dispatchEvent(event);
  }

  zoomOut() {
    const event = new CustomEvent('mapZoom', {
      detail: { direction: 'out' }
    });
    document.dispatchEvent(event);
  }

  updateTooltip(element, text) {
    if (!this.options.enableTooltips || !element) return;

    const tooltip = element.querySelector('.control-button-tooltip');
    if (tooltip) {
      tooltip.textContent = text;
    }
  }

  getDefaultTooltip(panelName) {
    const tooltips = {
      menu: 'Menu',
      filter: 'Filters',
      legend: 'Legend',
      search: 'Search'
    };
    return tooltips[panelName] || 'Toggle';
  }

  showNotification(message, type = 'info') {
    const event = new CustomEvent('showNotification', {
      detail: {
        message,
        type,
        duration: 3000
      }
    });
    document.dispatchEvent(event);
  }

  isPanelVisible(panelName) {
    const panel = this.panels.get(panelName);
    return panel ? panel.isVisible : false;
  }

  getActivePanel() {
    return this.activePanel;
  }

  getVisiblePanels() {
    const visible = [];
    this.panels.forEach((panel, name) => {
      if (panel.isVisible) {
        visible.push(name);
      }
    });
    return visible;
  }

  async loadStatistics() {
    try {
      // Import dataService dynamically to avoid circular dependencies
      const { dataService } = await import('../services/dataService.js');
      const response = await dataService.getStatistics();

      if (response && response.statistics) {
        const stats = response.statistics;

        // Update DOM elements with statistics
        const totalMinesEl = document.getElementById('stat-total-mines');
        const activeEl = document.getElementById('stat-active');
        const developmentEl = document.getElementById('stat-development');
        const explorationEl = document.getElementById('stat-exploration');
        const closedEl = document.getElementById('stat-closed');
        const commonEl = document.getElementById('stat-common');

        if (totalMinesEl) {
          totalMinesEl.textContent = (stats.total_mines || 0).toLocaleString();
        }

        // Display mine status counts directly
        if (stats.by_status) {
          const active = stats.by_status['Active'] || 0;
          const development = stats.by_status['Development'] || 0;
          const exploration = stats.by_status['Exploration'] || 0;
          const closed = stats.by_status['Closed'] || 0;

          if (activeEl) {
            activeEl.textContent = active.toLocaleString();
          }
          if (developmentEl) {
            developmentEl.textContent = development.toLocaleString();
          }
          if (explorationEl) {
            explorationEl.textContent = exploration.toLocaleString();
          }
          if (closedEl) {
            closedEl.textContent = closed.toLocaleString();
          }
        }

        // Most common commodity
        if (commonEl && stats.top_commodities && stats.top_commodities.length > 0) {
          commonEl.textContent = stats.top_commodities[0].commodity;
        }
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
      // Set error state in DOM
      const statElements = [
        'stat-total-mines', 'stat-active',
        'stat-development', 'stat-exploration', 'stat-closed', 'stat-common'
      ];
      statElements.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.textContent = 'Error';
      });
    }
  }

  destroy() {
    this.hideAllPanels();
    this.panels.clear();
    this.activePanel = null;
  }
}